/**
 * 障害物の輪郭ぼかし(#125)。
 *
 * 正本は [ADR-0034](../../../docs/adr/0034-obstacle-edge-blur.md)。
 *
 * ## なぜ「キャンバス幅に対する比率」で持つのか
 *
 * **表示 px では持てない。** 描画コアは S04 / V01 / V02 が共有し(設計書 7.4)、
 * **埋め込み先の幅はページごとに違う** —— `12px` と決め打つと、
 * 社内で確認した見た目と外部表示が一致しなくなる。
 *
 * **キャンバス px でもない。** 解像度を変えたとき、頂点は比率で持っているので
 * 形が保たれるのに**ぼけ方だけ変わる。** S04 は解像度の変更時に
 * 「頂点の位置は比率で保たれます」と告げており(#36)、**同じ操作で
 * 片方だけ変わるのは説明がつかない。**
 *
 * **読みにくさは UI で埋める。** レイヤーパネルには `blur × canvas.width` で
 * キャンバス px に換算して見せる —— **保存する形と見せる形を分ける**のは、
 * 分割の記法([ADR-0029](../../../docs/adr/0029-panel-split-grid.md))と同じ判断。
 *
 * **すべて純粋関数。** DOM も WebGL も触らない。
 */
import { roundCoordinate } from './coordinate.js';
/**
 * ぼかしの上限。**キャンバス幅の 5%**(FHD なら 96px 相当)。
 *
 * 輪郭をなじませる用途にはこれで十分すぎる。**上限を置かないと
 * 「マスクが消える」状態を作れてしまう** —— ぼかしがマスク全体を覆うと、
 * 障害物そのものが見えなくなり、**戻し方が分からない状態**になる。
 */
export const OBSTACLE_BLUR_MAX = 0.05;
/**
 * 保存・描画で使える形に正規化する。**未指定も不正な値もすべて 0**(ぼかさない)。
 *
 * **例外を投げない。** ここは描画の途中でも呼ばれる ——
 * 壊れた値 1 つでプレビュー全体が落ちるより、**ぼかさずに描くほうがよい。**
 * 入力そのものの検証は API の zod が担う(**2 段で止める**)。
 *
 * | 入力 | 結果 |
 * |---|---|
 * | `null` / `undefined` | `0` |
 * | `NaN` / `Infinity` | `0`(`roundCoordinate` と違い投げない) |
 * | `0` 以下 | `0` |
 * | [`OBSTACLE_BLUR_MAX`](#OBSTACLE_BLUR_MAX) 超え | `OBSTACLE_BLUR_MAX` |
 * | それ以外 | 小数第 6 位に丸める(座標と同じ精度) |
 */
export function normalizeBlur(value) {
    if (value == null || !Number.isFinite(value) || value <= 0)
        return 0;
    return roundCoordinate(Math.min(value, OBSTACLE_BLUR_MAX));
}
//# sourceMappingURL=blur.js.map